<script setup lang="ts">
import MinusIconCircle from '@/assets/svg/MinusIconCircle.vue'
import MoreIconCircle from '@/assets/svg/MoreIconCircle.vue'
import { getChargingTypeLabel } from '@/helpers/ChargerHelper'
import { getConnectorLabel, getConnectorStatusImage, mapAndFilterEvseConnectors } from '@/helpers/ConnectorHelper'
import type { IEvseStatus } from '@/models/EvsesStatus/IEvseStatus'
import type { IEvseStatusFiltered } from '@/models/EvsesStatus/IEvseStatusFiltered'
import type { ICharger } from '@/models/ICharger'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  chargers: ICharger[]
  collapsed: boolean
  bottomLegendBgClassName?: string
}>()

const evsesRef = ref<IEvseStatus[]>()
const evsesFilteredRef = ref<IEvseStatusFiltered[]>()
const connectorCountRef = ref<number>()
const collapsedRef = ref<boolean>(props.collapsed)
const { t } = useI18n()

const getAllConnectors = () => {
  const maxConnectors = 5

  evsesRef.value = props.chargers.flatMap(({ ChargePointId, Evses = [] }) => Evses?.map(evse => ({ Evse: evse, ChargePointId })) ?? []) ?? []

  evsesFilteredRef.value = mapAndFilterEvseConnectors(evsesRef.value, collapsedRef.value, maxConnectors)

  connectorCountRef.value = evsesRef.value?.reduce((count, evse) => count + (evse?.Evse.Connectors?.length ?? 0), 0) ?? 0
}

watch(
  () => props.chargers,
  () => {
    getAllConnectors()
  },
  { immediate: true }
)

onMounted(() => {
  getAllConnectors()
})

const handleToggleClick = () => {
  collapsedRef.value = !collapsedRef.value
  getAllConnectors()
}
</script>

<template>
  <div :class="`${props.collapsed ? 'flex' : 'flex flex-wrap'} flex-row gap-1`">
    <div v-for="(evse, index) in evsesFilteredRef" :key="index" class="flex gap-1 justify-content-center align-items-baseline">
      <fieldset class="flex flex-column evse-container evse-fieldset justify-content-center p-1 pb-2 gap-1">
        <legend class="align-items-center font-semibold">
          {{ getChargingTypeLabel(evse.EvseStatus.Evse.EvseModel?.ChargerModel?.ChargingType) }}
        </legend>
        <div class="flex gap-1 justify-content-center">
          <div v-for="(connector, index) in evse.Connectors" :key="index" class="connector-wrapper">
            <a :href="`/chargepointdetails/${evse?.EvseStatus.ChargePointId}`" target="_blank" rel="noopener noreferrer">
              <Image :src="getConnectorStatusImage(connector)" v-tooltip="getConnectorLabel(connector)" class="cursor-pointer" imageClass="max-w-none mt-2 mb-1" />
              <span class="connector-id-label !text-slate-500 dark:!text-slate-400">
                {{ connector.ElementId }}
              </span>
            </a>
          </div>
        </div>
        <legend :class="`evse-bottom-legend align-items-center text-xs font-semibold px-2 ${props.bottomLegendBgClassName || '!bg-white dark:!bg-surface-900'}`">
          {{ evse.EvseStatus.Evse.ElementId }}
        </legend>
      </fieldset>

      <div
        v-if="collapsedRef && connectorCountRef && evsesFilteredRef && connectorCountRef > 5 && index === evsesFilteredRef.length - 1"
        class="mt-2 flex align-items-center cursor-pointer"
        :key="9999"
        v-tooltip="{ value: `+${connectorCountRef - 5} ${t('general.connectors')}` }"
        @click="handleToggleClick"
      >
        <MoreIconCircle :count="connectorCountRef - 5" :data-cy="evse?.EvseStatus.ChargePointId + '-more-connectors'" />
      </div>

      <div
        v-if="props.collapsed && !collapsedRef && connectorCountRef && evsesFilteredRef && connectorCountRef > 5 && index === evsesFilteredRef.length - 1"
        class="mt-2 flex align-items-center cursor-pointer"
        :key="10000"
        v-tooltip="{ value: t('general.showLess') }"
        @click="handleToggleClick"
      >
        <MinusIconCircle data-cy="'less-connectors-' + props.identifier" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.evse-container {
  border-color: gray;
  border-width: 1.5px !important;
  border-style: solid;
  border-radius: 8px;
}

.evse-fieldset {
  position: relative;
  margin-bottom: 10px;
}

.evse-bottom-legend {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
}

.connector-wrapper {
  position: relative;
  display: inline-block;
}

.connector-wrapper a {
  position: relative;
  display: inline-block;
}

.connector-id-label {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: bold;
  z-index: 0;
  pointer-events: none;
}
</style>
